import { useState, useEffect, useCallback, type JSX } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import adminAPI from "./apiClient";
import type { Action, LLMRequest } from "./types";
import { formatJSON } from "./llmRequestFormatting";
import { ActionHeader } from "./ActionDetailPanels";
import ActionLlmRequests from "./ActionLlmRequests";
import DistillationTable from "./DistillationTable";
import { distillationGroups } from "./mcpDistillation";

interface ActionDetailState {
  action: Action | null;
  llmRequests: LLMRequest[];
  loading: boolean;
  error: string | null;
}

/** Loads an action and its LLM requests; `error` is surfaced as a banner, not a blocking state. */
function useActionDetail(
  conversationId: string | undefined,
  actionId: string | undefined,
): ActionDetailState {
  const [action, setAction] = useState<Action | null>(null);
  const [llmRequests, setLlmRequests] = useState<LLMRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActionDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Drop the action already on screen before fetching the next one: the
      // route reuses this component, so a failed load would otherwise leave the
      // previous action's requests rendered under the new action's URL.
      setAction(null);
      setLlmRequests([]);
      const actionData = await adminAPI.getAction(conversationId, actionId);
      setAction(actionData.action);
      setLlmRequests(actionData.llm_requests);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  }, [conversationId, actionId]);

  useEffect(() => {
    void fetchActionDetails();
  }, [fetchActionDetails]);

  return { action, llmRequests, loading, error };
}

export default function ActionDetail(): JSX.Element {
  const { conversationId, turnId, actionId } = useParams<{
    conversationId: string;
    turnId: string;
    actionId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromTab = searchParams.get("fromTab") || "actions";
  const { action, llmRequests, loading, error } = useActionDetail(conversationId, actionId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!action) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">Action not found.</div>
      </div>
    );
  }

  const actionPath = `/admin/conversation/${conversationId}/turn/${turnId}/action/${actionId}`;
  // One builder for both panels: the distillation table and the request list
  // link to the same page, and a second copy could only drift from it.
  const requestHref = (llmRequestId: string): string =>
    `${actionPath}/llm-request/${llmRequestId}?fromTab=${fromTab}`;
  const handleBackToTurn = (): void => {
    void navigate(`/admin/conversation/${conversationId}/turn/${turnId}?tab=${fromTab}`);
  };

  return (
    <div className="space-y-6">
      <ActionHeader
        action={action}
        llmRequestCount={llmRequests.length}
        error={error}
        onBack={handleBackToTurn}
      />

      {/* Above the raw JSON: for a distillation action the judgements ARE the
          output worth reading, and the JSON below it is the fallback. */}
      <DistillationTable
        groups={distillationGroups(llmRequests, action.action_output)}
        requestHref={requestHref}
      />

      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Action Output</h3>
        <div className="bg-gray-50 p-4 rounded-md">
          <pre className="text-sm text-gray-900 whitespace-pre-wrap overflow-x-auto">
            {formatJSON(action.action_output)}
          </pre>
        </div>
      </div>

      <ActionLlmRequests
        requests={llmRequests}
        actionOutput={action.action_output}
        detailHref={requestHref}
      />
    </div>
  );
}
